// src/services/dataAdapters/SupabaseDataAdapter.js
// DataAdapter implementation backed by a Supabase (PostgreSQL) database.
//
// Uses the same @supabase/supabase-js client as SupabaseBackend.
//
// Schema introspection note:
//   The Supabase JS client does not expose DDL / information_schema queries,
//   so listTables() and listColumns() return hardcoded allow-lists that mirror
//   a standard RMS demo database.  This is intentional for the demo phase.

import { createClient } from '@supabase/supabase-js';
import DataAdapter from './DataAdapter';

// ─────────────────────────────────────────────────────────────────────────────
// Hardcoded allow-list (Supabase JS client lacks DDL introspection)
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_TABLES = ['customers', 'transactions', 'alerts', 'rms_demo_customers'];

const TABLE_SCHEMA = {
  customers: [
    { name: 'id',           type: 'text',    nullable: false },
    { name: 'name',         type: 'text',    nullable: false },
    { name: 'country_code', type: 'text',    nullable: true  },
    { name: 'risk_score',   type: 'numeric', nullable: true  },
    { name: 'status',       type: 'text',    nullable: true  },
  ],
  transactions: [
    { name: 'id',           type: 'text',    nullable: false },
    { name: 'customer_id',  type: 'text',    nullable: false },
    { name: 'amount',       type: 'numeric', nullable: false },
    { name: 'currency',     type: 'text',    nullable: true  },
    { name: 'date',         type: 'text',    nullable: true  },
    { name: 'country_code', type: 'text',    nullable: true  },
  ],
  alerts: [
    { name: 'id',          type: 'text', nullable: false },
    { name: 'customer_id', type: 'text', nullable: false },
    { name: 'alert_type',  type: 'text', nullable: true  },
    { name: 'severity',    type: 'text', nullable: true  },
    { name: 'created_at',  type: 'text', nullable: true  },
  ],
  rms_demo_customers: [
    { name: 'customer_id',             type: 'text',    nullable: false },
    { name: 'full_name',               type: 'text',    nullable: false },
    { name: 'customer_type',           type: 'text',    nullable: false },
    { name: 'country',                 type: 'text',    nullable: false },
    { name: 'age',                     type: 'integer', nullable: true  },
    { name: 'pep_status',              type: 'boolean', nullable: false },
    { name: 'sanctions_match',         type: 'boolean', nullable: false },
    { name: 'high_risk_country',       type: 'boolean', nullable: false },
    { name: 'cash_intensive_industry', type: 'boolean', nullable: false },
    { name: 'onboarded_via_broker',    type: 'boolean', nullable: false },
    { name: 'no_swedish_history',      type: 'boolean', nullable: false },
    { name: 'young_customer',          type: 'boolean', nullable: false },
    { name: 'ownership_layers',        type: 'integer', nullable: true  },
    { name: 'cross_border_tx_count',   type: 'integer', nullable: true  },
    { name: 'transaction_volume_12m',  type: 'numeric', nullable: true  },
    { name: 'account_age_months',      type: 'integer', nullable: true  },
    { name: 'risk_category_external',  type: 'text',    nullable: false },
    { name: 'risk_score_external',     type: 'numeric', nullable: true  },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// SupabaseDataAdapter
// ─────────────────────────────────────────────────────────────────────────────

class SupabaseDataAdapter extends DataAdapter {
  /**
   * @param {{ url: string, key: string }} config
   *   url — Supabase project URL (e.g. https://xyz.supabase.co)
   *   key — Supabase anon or service-role API key
   */
  constructor(config = {}) {
    super();
    // Accept both `key` (SupabaseBackend-style) and `anonKey` (DataSourceService-style).
    const apiKey = config.key || config.anonKey || config.apiKey;
    this._client = createClient(config.url, apiKey);
  }

  get type() {
    return 'supabase';
  }

  // ── ping ──────────────────────────────────────────────────────────────────

  async ping() {
    const start = Date.now();
    try {
      const { error } = await this._client
        .from(ALLOWED_TABLES[0])
        .select('id')
        .limit(1);
      const latencyMs = Date.now() - start;
      if (error) {
        return { success: false, latencyMs, error: error.message };
      }
      return { success: true, latencyMs };
    } catch (err) {
      return { success: false, latencyMs: Date.now() - start, error: err.message };
    }
  }

  // ── listTables / listColumns ──────────────────────────────────────────────

  async listTables() {
    return [...ALLOWED_TABLES];
  }

  async listColumns(table) {
    return TABLE_SCHEMA[table] ? [...TABLE_SCHEMA[table]] : [];
  }

  // ── query ─────────────────────────────────────────────────────────────────

  async query({ table, columns, filter, limit } = {}) {
    const selectExpr = columns && columns.length > 0 ? columns.join(',') : '*';
    let builder = this._client.from(table).select(selectExpr);

    if (filter) {
      for (const [key, val] of Object.entries(filter)) {
        builder = builder.eq(key, val);
      }
    }

    if (limit && limit > 0) {
      builder = builder.limit(limit);
    }

    const { data, error } = await builder;
    if (error) throw new Error(`SupabaseDataAdapter.query error: ${error.message}`);
    return data || [];
  }

  // ── count ─────────────────────────────────────────────────────────────────

  async count({ table, filter } = {}) {
    let builder = this._client
      .from(table)
      .select('*', { count: 'exact', head: true });

    if (filter) {
      for (const [key, val] of Object.entries(filter)) {
        builder = builder.eq(key, val);
      }
    }

    const { count, error } = await builder;
    if (error) throw new Error(`SupabaseDataAdapter.count error: ${error.message}`);
    return count || 0;
  }

  // ── schemaCapabilities ────────────────────────────────────────────────────

  async schemaCapabilities() {
    return {
      canListTables:      true,   // allow-list is available
      canListColumns:     true,   // hardcoded column definitions are available
      canIntrospectTypes: false,  // Supabase JS client lacks DDL introspection
      supportsFilter:     true,   // .eq() filter is supported
    };
  }
}

export default SupabaseDataAdapter;
